import type { AppDispatch } from "../../../common/state/store";
import { updateMediaPushProgress } from "../state/deviceInstallerSlice";
import { AGENT_BASE_URL, AGENT_WS_URL } from "../constants";
import type { MediaManifestItem } from "./adbMediaPusher";

export interface MediaPushResult {
  pushed: number;
  failed: number;
  skipped: number;
}

interface AgentMediaStatusResult {
  mediaId: number;
  isUpToDate: boolean;
}

interface AgentErrorResponse {
  error: string;
}

interface AgentJobIdResponse {
  jobId: string;
}

interface AgentMediaProgressPushing {
  status: "pushing";
  pushedFiles: number;
  totalFiles: number;
  currentFileName: string;
  currentFileProgress: number;
}

interface AgentMediaProgressSuccess {
  status: "success";
  pushed: number;
  failed: number;
  skipped: number;
  totalFiles: number;
  pushedFiles: number;
}

interface AgentMediaProgressCancelled {
  status: "cancelled";
}

interface AgentMediaProgressError {
  status: "error";
  errorMessage: string;
}

type AgentMediaProgressData =
  | AgentMediaProgressPushing
  | AgentMediaProgressSuccess
  | AgentMediaProgressCancelled
  | AgentMediaProgressError;

interface AgentMediaProgressMessage {
  jobId: string;
  type: string;
  data: AgentMediaProgressData;
}

/** Scan which medias are already present on the desktop app's directory */
export async function scanAgentMediaStatus(
  appId: string,
  medias: MediaManifestItem[],
): Promise<Map<number, boolean>> {
  const result = new Map<number, boolean>();

  try {
    const res = await fetch(`${AGENT_BASE_URL}/media-status/${appId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ medias }),
    });

    if (res.ok) {
      const { results } = (await res.json()) as { results: AgentMediaStatusResult[] };
      for (const r of results) {
        result.set(r.mediaId, r.isUpToDate);
      }
    }
  } catch (err) {
    console.warn("[DesktopAgent] media status scan failed:", err);
  }

  return result;
}

/** Clear all media for an app via the agent */
export async function clearAgentMedia(appId: string): Promise<boolean> {
  try {
    const res = await fetch(`${AGENT_BASE_URL}/media/${appId}`, { method: "DELETE" });
    return res.ok;
  } catch {
    return false;
  }
}

/** Push medias via the desktop agent, listening to WebSocket for progress */
export async function pushMediasViaAgent(
  appId: string,
  medias: MediaManifestItem[],
  dispatch: AppDispatch,
  signal: AbortSignal,
): Promise<MediaPushResult> {
  const res = await fetch(`${AGENT_BASE_URL}/media-push/${appId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ medias }),
  });

  if (!res.ok) {
    const err = (await res
      .json()
      .catch(() => ({ error: "Agent media push request failed" }))) as AgentErrorResponse;
    throw new Error(err.error);
  }

  const { jobId } = (await res.json()) as AgentJobIdResponse;

  return new Promise<MediaPushResult>((resolve, reject) => {
    const ws = new WebSocket(AGENT_WS_URL);

    const cleanup = () => {
      ws.close();
    };

    const onAbort = () => {
      fetch(`${AGENT_BASE_URL}/media-push/${jobId}`, { method: "DELETE" }).catch(() => {});
      cleanup();
      reject(new DOMException("Cancelled", "AbortError"));
    };

    signal.addEventListener("abort", onAbort, { once: true });

    ws.onmessage = (event) => {
      let msg: AgentMediaProgressMessage;
      try {
        msg = JSON.parse(event.data as string) as AgentMediaProgressMessage;
      } catch (err) {
        console.warn("[DesktopAgent] unreadable media push message:", err);
        return;
      }
      if (msg.jobId !== jobId || msg.type !== "media-progress") return;

      const data = msg.data;

      switch (data.status) {
        case "pushing":
          dispatch(
            updateMediaPushProgress({
              pushedFiles: data.pushedFiles,
              currentFileName: data.currentFileName,
              currentFileProgress: data.currentFileProgress,
            }),
          );
          break;

        case "success":
          signal.removeEventListener("abort", onAbort);
          cleanup();
          resolve({
            pushed: data.pushed || 0,
            failed: data.failed || 0,
            skipped: data.skipped || 0,
          });
          break;

        case "cancelled":
          signal.removeEventListener("abort", onAbort);
          cleanup();
          reject(new DOMException("Cancelled", "AbortError"));
          break;

        case "error":
          signal.removeEventListener("abort", onAbort);
          cleanup();
          reject(new Error(data.errorMessage || "Media push failed"));
          break;
      }
    };

    ws.onerror = () => {
      signal.removeEventListener("abort", onAbort);
      reject(new Error("WebSocket connection to agent lost"));
    };
  });
}
